import { RefObject, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { playbackRateSelector } from '../../../../store/bite/bite.selectors';
import { setPlaybackRate } from '../../../../store/bite/bite.actions';
import ReactPlayer from 'react-player';

type Props = {
  playerRef: RefObject<ReactPlayer>;
  withReset?: boolean;
};
export const usePlaybackRate = ({ playerRef, withReset }: Props) => {
  const dispatch = useDispatch();

  const playbackRate = useSelector(playbackRateSelector);

  const signalRef = useRef<AbortController>();

  const trackPlaybackRate = useCallback(() => {
    signalRef.current?.abort();

    const video = playerRef.current?.getInternalPlayer() as HTMLVideoElement;
    if (!video) {
      return;
    }

    // set initial
    video.playbackRate = playbackRate;

    signalRef.current = new AbortController();
    const handleRateChange = () => {
      dispatch(setPlaybackRate(video.playbackRate));
    };

    video.addEventListener('ratechange', handleRateChange, {
      signal: signalRef.current.signal,
    });
  }, [dispatch, playbackRate, playerRef]);

  useEffect(() => {
    // sync with the store value
    const video = playerRef.current?.getInternalPlayer() as HTMLVideoElement;
    if (video) {
      video.playbackRate = playbackRate;
    }
  }, [playbackRate, playerRef]);

  useEffect(() => {
    if (withReset) {
      dispatch(setPlaybackRate(1));
    }

    return () => {
      signalRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    playbackRate,
    trackPlaybackRate,
  };
};
